<!-- GenerateCIFLoadReportPDF.vue -->
<template>
  <v-btn color="primary" @click="generatePDF">
    <slot>Generate CIF PDF</slot>
  </v-btn>
</template>

<script>
import { jsPDF } from 'jspdf'
import { useRoute } from 'vue-router'
import { query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import { misconductColRef, matchreportsColRef } from '@/firebase'

export default {
  name: 'GenerateCIFLoadReport',
  props: {
    mreportId: { type: String, default: null },
    match: { type: Object, default: null },
    reports: { type: Array, default: null },

    /* === NEW: explicit 2-col orders === */
    fieldOrderYC_LeftCol: {
      type: Array,
      default: () => [
        'Sport','DateOfIncident','GameSite',
        'VisitingTeam', 'Offender', 'PlayerNumber', 'PlayerNameLast',
        'MisconductItem', 'CIFCode', 'IncidentDescription',             
      ]
    },
    fieldOrderYC_RightCol: {
      type: Array,
      default: () => [
        'Level', 'Time', 'HomeTeam', 'WinningSchoolName', 'PlayerNameFirst', 'Team', 'School',
      ]
    },
    fieldOrderRC_LeftCol: {
      type: Array,
      default: () => [
      'Sport','DateOfIncident','GameSite', 'VisitingTeam', /* Final Score */  
      'Offender', 'PlayerNameLast', 'PlayerNumber',
      'MisconductItem','MisconductCode',
      'IncidentDescription', /* warned before ? */
      'OfficialFirstName','OfficialLastName','OfficialEmail','OfficialPhone' 
      ]
    },
    fieldOrderRC_RightCol: {
      type: Array,
      default: () => [
        'Level', 'Time', 'HomeTeam', /* HT score at event */ 'RefereeAction','CIFCode',
        'PlayerNameFirst','Team','School',
        
        'OfficialFirstName','OfficialLastName','OfficialEmail','OfficialPhone'
      ]
    },
  },
  setup(props) {
    const route = useRoute()

    const ALIASES = {
      'date of incident': 'DateOfIncident',
      'game site': 'GameSite',
      'home team': 'HomeTeam',
      'visiting team': 'VisitingTeam',
      'winning school name': 'WinningSchoolName',
      'player #': 'PlayerNumber',
      'playernumber': 'PlayerNumber',
      playernumber: 'PlayerNumber',
      'misconduct code': 'MisconductCode',
      'cif code': 'CIFCode',
      'referee action': 'RefereeAction',
      'match level': 'GenderAge',
      sport: 'Sport',
      level: 'Level',
      time: 'Time',
      offender: 'Offender',
      team: 'School',
      school: 'School',
      playernamefirst: 'PlayerNameFirst',
      'player name first': 'PlayerNameFirst',
      playernamelast: 'PlayerNameLast',
      'player name last': 'PlayerNameLast',
      incidenttype: 'IncidentType',
      'incident description': 'IncidentDescription',
      league: 'League',
      organization: 'Organization',
      'game #': 'GameNumber',
      'game number': 'GameNumber',
      'official first name': 'OfficialFirstName',
      'official last name':  'OfficialLastName',
      'official email':      'OfficialEmail',
      'official phone':      'OfficialPhone',
    }
    const normalizeKey = (k) => {
      if (!k) return k
      const raw = k.toString().trim()
      const alias = ALIASES[raw.toLowerCase().replace(/\s+/g, ' ')]
      return alias || raw
    }

    const isRedLike = (r) => /red|eject|send\s?-?off/i.test(String(r?.RefereeAction || ''))

    const uniqueMerge = (left, right) => {
      // prevent accidental duplicates across the two columns; keep first occurrence
      const seen = new Set()
      const uniq = (arr) => arr.filter(k => (k && !seen.has(k) && seen.add(k)))
      return { left: uniq(left), right: uniq(right) }
    }

    const getOrdersForReport = (r) => {
      const leftRaw  = isRedLike(r) ? props.fieldOrderRC_LeftCol  : props.fieldOrderYC_LeftCol
      const rightRaw = isRedLike(r) ? props.fieldOrderRC_RightCol : props.fieldOrderYC_RightCol
      const left  = leftRaw.map(normalizeKey)
      const right = rightRaw.map(normalizeKey)
      return uniqueMerge(left, right)
    }

    const getMreportId = () => props.mreportId || route.params.mreportId || route.params.id || null

    const fetchMatch = async (id) => {
      if (props.match) return props.match
      if (!id) return null
      const snap = await getDoc(doc(matchreportsColRef, id))
      return snap.exists() ? snap.data() : null
    }

    const fetchMisconducts = async (id) => {
      if (Array.isArray(props.reports) && props.reports.length) return props.reports
      if (!id) return []
      const q1 = query(misconductColRef, where('MatchReportId', '==', id))
      const q2 = query(misconductColRef, where('MatchReportID', '==', id))
      const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)])
      const map = new Map()
      const add = (snap) => snap.forEach(d => map.set(d.id, { id: d.id, ...d.data() }))
      add(s1); add(s2)
      const parseTime = (t) => {
        if (t == null) return Number.POSITIVE_INFINITY
        const s = String(t)
        const parts = s.split('+')
        const base = parseInt(parts[0], 10) || 0
        const added = parts[1] ? (parseInt(parts[1], 10) || 0) : 0
        return base * 100 + added
      }
      return Array.from(map.values()).sort((a, b) => parseTime(a.Time) - parseTime(b.Time))
    }

    const firstNonEmpty = (...vals) => {
      for (const v of vals) if (typeof v === 'string' && v.trim()) return v.trim()
      return ''
    }

    // GA helpers
    const normalizeGA = (ga) => (ga || '').toString().trim().toUpperCase()
    const deriveSport = (ga) => {
      const g = normalizeGA(ga)
      if (['VB','JVB','FSB'].includes(g)) return 'Soccer, Boys'
      if (['VG','JVG','FSG'].includes(g)) return 'Soccer, Girls'
      return ''
    }
    const deriveLevel = (ga) => {
      const g = normalizeGA(ga)
      if (g === 'VB' || g === 'VG') return 'Varsity'
      if (g === 'JVB' || g === 'JVG') return 'Junior Varsity'
      if (g === 'FSB' || g === 'FSG') return 'Frosh/Soph'
      return ''
    }

    const deriveDateOfIncident = (match) => formatDate(match?.GameDate)
    const deriveGameSite = (match) => firstNonEmpty(match?.TeamHome, match?.HomeTeam)
    const deriveHomeTeam = (match) => firstNonEmpty(match?.TeamHome, match?.HomeTeam)
    const deriveVisitingTeam = (match) => firstNonEmpty(match?.TeamAway, match?.VisitingTeam, match?.Away)
    const deriveWinningSchoolName = (match) => {
      const sh = Number(match?.ScoreHome), sa = Number(match?.ScoreAway)
      const home = deriveHomeTeam(match), away = deriveVisitingTeam(match)
      if (!Number.isFinite(sh) || !Number.isFinite(sa)) return ''
      if (sh > sa) return home || ''
      if (sa > sh) return away || ''
      return 'Tied'
    }

    const normalize = (s) => (s || '').toString().trim().toLowerCase()
    const equals = (a,b) => normalize(a) === normalize(b)
    const deriveSchool = (match, r) => {
      const home = firstNonEmpty(match?.TeamHome, match?.HomeTeam)
      const away = firstNonEmpty(match?.TeamAway, match?.VisitingTeam, match?.Away)
      const tRaw = r?.Team || ''
      const t = normalize(tRaw)
      if (['home','h','home team','teamhome'].includes(t)) return home || ''
      if (['away','a','visitor','visiting','visiting team','teamaway'].includes(t)) return away || ''
      if (equals(tRaw, home)) return home || ''
      if (equals(tRaw, away)) return away || ''
      return tRaw || ''
    }

    const formatDate = (date) => {
      try {
        if (date == null) return 'N/A'
        if (typeof date === 'object') {
          if (typeof date.toDate === 'function') date = date.toDate()
          else if ('seconds' in date && Number.isFinite(date.seconds)) date = new Date(date.seconds * 1000)
        } else if (typeof date === 'number') date = new Date(date)
        else if (typeof date === 'string') {
          const d = new Date(date)
          if (!Number.isNaN(d.getTime())) date = d
          else return 'N/A'
        }
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'N/A'
        const mm = String(date.getMonth()+1).padStart(2,'0')
        const dd = String(date.getDate()).padStart(2,'0')
        const yyyy = String(date.getFullYear())
        return `${mm}/${dd}/${yyyy}`
      } catch { return 'N/A' }
    }

    const formatDateForFile = (date) => {
      if (!date) return 'N_A'
      try {
        let d = date
        if (typeof date === 'object' && typeof date.toDate === 'function') d = date.toDate()
        else if (typeof date === 'string') d = new Date(date)
        else if (date && date.seconds) d = new Date(date.seconds * 1000)
        const year = d.getFullYear()
        const month = String(d.getMonth()+1).padStart(2,'0')
        const day = String(d.getDate()).padStart(2,'0')
        return `${year}${month}${day}`
      } catch { return 'N_A' }
    }

    const ensurePage = (doc, y, bottom = 15, top = 10) => {
      const pageH = doc.internal.pageSize.getHeight()
      if (y > pageH - bottom) {
        doc.addPage()
        return top
      }
      return y
    }

    const splitName = (full = '') => {
      const s = String(full).trim()
      if (!s) return { first: '', last: '' }
      const parts = s.split(/\s+/)
      if (parts.length === 1) return { first: parts[0], last: '' }
      return { first: parts.slice(0, -1).join(' '), last: parts.slice(-1)[0] }
    }

    const label = (k) => ({
      Sport: 'Sport',
      Level: 'Level',
      DateOfIncident: 'Date of Incident',
      GameSite: 'Game Site',
      HomeTeam: 'Home Team',
      VisitingTeam: 'Visiting Team',
      WinningSchoolName: 'Winning School',
      Time: 'Time',
      Offender: 'Offender',
      Team: 'Team',
      School: 'School',
      PlayerNumber: 'Player #',
      PlayerNameLast: 'Last Name',
      PlayerNameFirst: 'First Name',
      IncidentType: 'Incident Type',
      IncidentDescription: 'Description',
      RefereeAction: 'Referee Action',
      MisconductItem: 'Misconduct',
      MisconductCode: 'Misconduct Code',
      CIFCode: 'CIF Code',
      League: 'League',
      GenderAge: 'Match Level',
      Organization: 'Org',
      GameNumber: 'Game #',
      OfficialFirstName: 'Official First Name',
      OfficialLastName: 'Official Last Name',
      OfficialEmail: 'Official Email',
      OfficialPhone: 'Official Phone',
    }[k] || k)

    const PAGE = { margin: 10, gutter: 8, labelW: 30, lineH: 5.2, bottom: 15 }
    const INNER = { gutter: 6 }

    const drawHeader = (doc, margin, match) => {
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(12)
      doc.text('CIF MISCONDUCT LOAD REPORT', 105, margin, { align: 'center' })

      const y1 = margin + 7
      doc.setFontSize(10)
      doc.text('Date:', margin, y1)
      doc.text(formatDate(match?.GameDate), margin + 18, y1)

      doc.text('Game #:', margin + 45, y1)
      doc.text(String(match?.GameNumber ?? 'N/A'), margin + 65, y1)

      doc.text('Teams', margin + 95, y1)
      doc.text('(home)', margin + 80, y1 + 8)
      doc.text(String(match?.TeamHome ?? 'N/A'), margin + 95, y1 + 8)
      doc.text('(away)', margin + 80, y1 + 15)
      doc.text(String(match?.TeamAway ?? 'N/A'), margin + 95, y1 + 15)

      doc.text('Final Score', margin + 148, y1)
      doc.text(`${match?.ScoreHome ?? 'N/A'} - ${match?.ScoreAway ?? 'N/A'}`, margin + 148, y1 + 8)

      const y2 = y1 + 25
      doc.text(`Match Level: ${match?.GenderAge ?? 'N/A'}`, margin, y2)
      doc.text(`League: ${match?.LeagueTypeSeason ?? match?.League ?? 'N/A'}`, margin + 60, y2)

      const y3 = y2 + 12
      doc.text(`REF: ${match?.REFName ?? 'N/A'}`, margin, y3)
      doc.text(`AR1: ${match?.AR1Name ?? 'N/A'}`, margin + 60, y3)
      doc.text(`AR2: ${match?.AR2Name ?? 'N/A'}`, margin + 110, y3)
      doc.text(`4th REF: ${match?.REF4THName ?? match?.REF4thName ?? 'N/A'}`, margin + 155, y3)

      doc.setLineWidth(0.5)
      doc.line(margin, y3 + 6, 200, y3 + 6)

      return y3 + 12
    }

    const layoutTwoColFixed = (doc, merged, leftKeys, rightKeys, colWidth) => {
      const innerWidth = (colWidth - INNER.gutter) / 2
      const textW = innerWidth - PAGE.labelW

      const makeItem = (k) => {
        const text = String(merged[k] ?? 'N/A')
        const wrapped = doc.splitTextToSize(text, textW)
        const height = Math.max(6, wrapped.length * PAGE.lineH)
        return { key: k, wrapped, height }
      }

      const left  = leftKeys.map(makeItem)
      const right = rightKeys.map(makeItem)
      const leftH  = left.reduce((a, it) => a + it.height, 0)
      const rightH = right.reduce((a, it) => a + it.height, 0)
      const bodyH  = Math.max(leftH, rightH)
      return { left, right, innerWidth, textW, bodyH }
    }

    const drawMisconduct = (doc, x, y, index, merged, leftKeys, rightKeys, colWidth) => {
      const { left, right, innerWidth } = layoutTwoColFixed(doc, merged, leftKeys, rightKeys, colWidth)

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.text(`Misconduct ${index + 1}`, x, y)
      y += 6

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.text(`Type: ${merged?.RefereeAction || 'N/A'}`, x, y)
      y += 6

      const xLeft  = x
      const xRight = x + innerWidth + INNER.gutter
      let yLeft = y
      let yRight = y

      const drawField = (fx, fy, it) => {
        const k = it.key
        doc.text(`${label(k)}:`, fx, fy)
        doc.text(it.wrapped, fx + PAGE.labelW, fy)
        return fy + Math.max(6, it.wrapped.length * PAGE.lineH)
      }

      for (const it of left)  yLeft  = drawField(xLeft,  yLeft,  it)
      for (const it of right) yRight = drawField(xRight, yRight, it)

      return Math.max(yLeft, yRight) + 2
    }

    const buildFilename = (match) => {
      const datePart = formatDateForFile(match?.GameDate)
      const game = match?.GameNumber ?? 'N_A'
      return `${datePart}_CIF_Load_Game#${game}.pdf`
    }

    const buildMergedForReport = (r, match) => {
      const genderAge = match?.GenderAge ?? r?.GenderAge
      const refFull = match?.REFName ?? r?.REFName ?? ''
      const { first: refFirst, last: refLast } = splitName(refFull)

      // 🔍 DEBUG: Log the PlayerNumber from the misconduct report
      console.log('🔍 Building merged data for misconduct report:')
      console.log('  - Report data:', r)
      console.log('  - PlayerNumber from report:', r?.PlayerNumber)
      console.log('  - All report keys:', Object.keys(r))

      return {
        ...r,
        League: match?.League ?? r?.League,
        GenderAge: genderAge,
        Organization: match?.Organization ?? r?.Organization,
        GameNumber: match?.GameNumber ?? r?.GameNumber,

        Sport: deriveSport(genderAge),
        Level: deriveLevel(genderAge),
        DateOfIncident: deriveDateOfIncident(match),
        GameSite: deriveGameSite(match),
        HomeTeam: deriveHomeTeam(match),
        VisitingTeam: deriveVisitingTeam(match),
        WinningSchoolName: deriveWinningSchoolName(match),
        School: deriveSchool(match, r),

        // ✅ EXPLICITLY preserve PlayerNumber
        PlayerNumber: r?.PlayerNumber ?? 'N/A',

        REFName: refFull,
        OfficialFirstName: refFirst,
        OfficialLastName: refLast,
        OfficialEmail: '',
        OfficialPhone: '',
      }
    }

    const generatePDF = async () => {
      const id = getMreportId()
      const match = await fetchMatch(id)
      const reports = await fetchMisconducts(id)
      if (!match) { console.error('No match report found'); return }

      console.log('🔍 Generating PDF for match:', match?.GameNumber)
      console.log('🔍 Number of misconduct reports:', reports.length)

      const doc = new jsPDF()
      const margin = PAGE.margin
      const pageW = doc.internal.pageSize.getWidth()
      const colWidth = pageW - margin * 2

      if (!reports.length) {
        drawHeader(doc, margin, match)
        doc.setFontSize(10)
        doc.text('No misconduct reports found.', margin, margin + 40)
        return doc.save(buildFilename(match))
      }

      reports.forEach((r, i) => {
        console.log(`🔍 Processing misconduct report ${i + 1}:`)
        console.log('  - PlayerNumber:', r?.PlayerNumber)
        console.log('  - PlayerNameFirst:', r?.PlayerNameFirst)
        console.log('  - PlayerNameLast:', r?.PlayerNameLast)
        
        if (i > 0) doc.addPage()
        let y = drawHeader(doc, margin, match)

        const merged = buildMergedForReport(r, match)
        console.log('  - Merged PlayerNumber:', merged?.PlayerNumber)
        
        const { left, right } = getOrdersForReport(r)
        console.log('  - Left column keys:', left)
        console.log('  - Right column keys:', right)
        console.log('  - PlayerNumber in left?', left.includes('PlayerNumber'))
        console.log('  - PlayerNumber in right?', right.includes('PlayerNumber'))

        y = drawMisconduct(doc, margin, y, i, merged, left, right, colWidth)
        y = ensurePage(doc, y)
      })

      doc.save(buildFilename(match))
    }

    return { generatePDF }
  },
}
</script>


<style scoped>
button {
  cursor: pointer;
  font-size: 14px;
  padding: 8px 12px;
  color: #fff;
  background-color: #007bff;
  border: none;
  border-radius: 4px;
}
button:hover { background-color: #0056b3; }
</style>